import { ChangeEvent, FormEventHandler, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Input,
  LinearProgress,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

type UploadAreaProps = {
  file: File | null;
  inProgress?: boolean;
  progressMessage?: string;
  onUploadChange: FormEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

// TODO: Cleanup the whole file
export const UploadArea = ({
  file,
  inProgress,
  onUploadChange,
}: UploadAreaProps) => {
  const intl = useIntl();
  const [dragging, setDragging] = useState(false);

  const inputProps = {
    type: 'file',
    multiple: false,
    accept: [
      'application/json',
      'application/xml',
      'text/xml',
      'text/csv',
      '.csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].join(),
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      onUploadChange(e);
      // So picking the same file again, e.g. once it is fixed, is a change.
      e.currentTarget.value = '';
    },
  };

  return (
    <Box
      component="span"
      onDragEnter={() => setDragging(true)}
      sx={{
        py: 3,
        my: 2,
        mx: 2,
        border: '1px dashed',
        borderRadius: borderRadius('m'),
        display: 'flex',
        alignSelf: 'stretch',
        alignContent: 'center',
        justifyContent: 'center',
        borderColor: dragging ? 'primary.main' : 'text.disabled',
        position: 'relative',
      }}
    >
      <Stack spacing={1} alignItems="center" my={1} justifyContent="center">
        {inProgress ? (
          <LinearProgress />
        ) : (
          <>
            {file && (
              <Typography
                fontSize="small"
                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
              >
                {intl.formatMessage(
                  { id: 'features.datasets.uploadFile.selectedFile' },
                  { file: file.name }
                )}
              </Typography>
            )}
            <Button variant="outlined" color="secondary" component="label">
              <Typography fontSize="inherit">Choose File</Typography>
              <Input
                key={`input-button-${file?.name}`}
                inputProps={inputProps}
                sx={{
                  display: 'none',
                }}
              />
            </Button>
            <Typography variant="caption" px={4} pt={1}>
              {intl.formatMessage({
                id: 'features.datasets.uploadFile.dragAndDrop',
              })}
            </Typography>
          </>
        )}
      </Stack>
      <input
        {...inputProps}
        key={`input-drag-and-drop-${file?.name}`}
        onDragLeave={() => setDragging(false)}
        onDrop={() => setDragging(false)}
        style={{
          display: 'none',
          top: 0,
          ...(dragging && {
            position: 'absolute',
            display: 'block',
            opacity: 0,
            height: '100%',
            width: '100%',
            background: 'white',
            zIndex: 1,
          }),
        }}
      />
    </Box>
  );
};
